import { useEffect, useState } from 'react'
import { Grid, Image } from 'semantic-ui-react'
import { useNavigate } from 'react-router-dom'

import { useLoadedFlagValue, useReselector } from '../../utils/sharedHooks'
import {
  GET_ZENDESK_SSO_KEY,
  sendVerificationEmail,
  getZendeskSso,
} from '../../actions/authActions'
import { getFetchError, getIsFetching } from '../../reducers/fetch'
import {
  Loader,
  GridRowColumn,
  Alert,
  Button,
  Text,
  Card,
} from '../../components/BaseComponents'
import { useZendeskSso } from './helpers'
import { useAppDispatch } from '../../utils/typeHelpers'
import { isEmailVerified } from '../../selectors/user.selectors'
import { Colors } from '../../styles/theme'
import { useAnalyticsTrack } from '../Amplitude'
import { FEATURE_FLAG_KEYS } from '../OpenFeature'

const ZendeskSso = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const error = useReselector(getFetchError, GET_ZENDESK_SSO_KEY)
  const isFetching = useReselector(getIsFetching, GET_ZENDESK_SSO_KEY)
  const userEmailVerified = useReselector(isEmailVerified)
  const { zendeskForm, submitForm } = useZendeskSso()
  const [emailSent, setEmailSent] = useState(false)

  const zendeskEmailVerificationEnabled = useLoadedFlagValue(
    FEATURE_FLAG_KEYS.enableZendeskEmailVerificationFlow,
    false
  )

  useEffect(() => {
    if (
      !userEmailVerified &&
      !isFetching &&
      typeof zendeskEmailVerificationEnabled === 'boolean'
    ) {
      track('zendesk message access blocked', {
        reason: 'unverified_email',
        feature_flag_enabled: zendeskEmailVerificationEnabled,
      })
    }
  }, [userEmailVerified, isFetching, track, zendeskEmailVerificationEnabled])

  useEffect(() => {
    const fetch = async () => {
      if (userEmailVerified) {
        const res = await dispatch(getZendeskSso())
        if (res) {
          submitForm(res.data.jwt)
        }
      }
    }

    fetch()
  }, [dispatch, submitForm, userEmailVerified])

  const handleResendEmail = async () => {
    await dispatch(sendVerificationEmail())
    setEmailSent(true)
  }

  if (!userEmailVerified && zendeskEmailVerificationEnabled) {
    return (
      <>
        <Loader loading={isFetching} />
        <Grid textAlign="center" style={{ paddingTop: '3em', width: '100%' }}>
          <GridRowColumn centerContent>
            <Image
              src="https://heard-images.s3.us-west-1.amazonaws.com/heard_logo_green_2x.png"
              alt="heard logo"
              style={{ height: 20 }}
            />
          </GridRowColumn>
          <GridRowColumn>
            <Text as="h2">Email Verification Required</Text>
          </GridRowColumn>
          <GridRowColumn centered computer={8} mobile={15} tablet={12}>
            <Card fullWidth>
              <Grid>
                <GridRowColumn>
                  <Text as="h3" style={{ color: Colors.red }}>
                    Your email address needs to be verified before you can
                    access your messages.
                  </Text>
                </GridRowColumn>
                <GridRowColumn>
                  <Text>
                    For security reasons, we require all users to verify their
                    email address before accessing messages.
                  </Text>
                </GridRowColumn>
                {emailSent ? (
                  <GridRowColumn>
                    <Alert type="success">
                      <Text>
                        <strong>Verification email sent!</strong>
                      </Text>
                      <Text>
                        Please check your inbox and click the verification link
                        in the email we just sent.
                      </Text>
                    </Alert>
                  </GridRowColumn>
                ) : (
                  <GridRowColumn>
                    <Button fullWidth onClick={handleResendEmail}>
                      Send Verification Email
                    </Button>
                  </GridRowColumn>
                )}
                <GridRowColumn>
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => navigate('/dashboard')}
                  >
                    Return to Dashboard
                  </Button>
                </GridRowColumn>
              </Grid>
            </Card>
          </GridRowColumn>
          {emailSent && (
            <GridRowColumn>
              <Text>
                Haven&apos;t received the verification email? Check your spam
                folder or{' '}
                <Button variant="link" onClick={handleResendEmail}>
                  click here to send it again
                </Button>
              </Text>
            </GridRowColumn>
          )}
        </Grid>
      </>
    )
  }

  if (error) {
    return (
      <Alert type="error" title="Error">
        <Text>{error?.message}</Text>
        <Button onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </Button>
      </Alert>
    )
  }

  return (
    <>
      <Loader />
      {zendeskForm}
    </>
  )
}

export default ZendeskSso
